import { themeQuartz, type ColDef } from "ag-grid-community"
import { formatWithPrecision, roundWithPrecision } from "./numberPrecisionFormat"
import { getPrecisionSettings } from "./usePrecisionConfiguration"

/**
 * Default column options for every result grid: Excel-style filtering opened
 * from the filter button in the column header, with no extra filter row.
 */
export const resultGridDefaultColDef: ColDef = {
	wrapHeaderText: true,
	autoHeaderHeight: true,
	filter: true,
	floatingFilter: false,
	suppressHeaderMenuButton: false,
	suppressHeaderFilterButton: false,
}

/**
 * Excel-style checkbox list filter, for descriptive text columns.
 */
export const textFilterColDef: Partial<ColDef> = {
	filter: "agSetColumnFilter",
	filterParams: {
		excelMode: "windows",
		buttons: ["reset"],
	},
}

function parseFormattedNumber(text: string | null): number | null {
	if (text == null || text.trim() === "") return null
	const value = Number(text.replace(/,/g, ""))
	return Number.isFinite(value) ? value : null
}

export function valueToRoundedNumber(value: number | string | null): number | null {
	if (typeof value === "string") value = parseFormattedNumber(value)
	return value == null || !Number.isFinite(value) ? null : roundWithPrecision(value, getPrecisionSettings())
}

/**
 * Numeric comparison filter (greater than, less than, between, ...).
 * Filtering uses the rounded numeric value users see in result tables.
 */
export const numberFilterColDef: Partial<ColDef> = {
	filter: "agNumberColumnFilter",
	filterValueGetter: (params) => valueToRoundedNumber(params.getValue(params.column.getColId())),
	filterParams: {
		allowedCharPattern: "\\d\\-\\+\\.eE,",
		numberParser: parseFormattedNumber,
		maxNumConditions: 2,
		buttons: ["reset"],
	},
}

// to use resultGridTheme in an application, pass it to the theme grid option
export const resultGridTheme = themeQuartz.withParams({
	browserColorScheme: "light",
	fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
	headerFontSize: 14,
	rowVerticalPaddingScale: 0.75,
})

export type ValueToStringFormatterOptions = {
	treatNullAsZero?: boolean
}

export function valueToStringFormatter(
	value: number | string | null,
	{ treatNullAsZero = false }: ValueToStringFormatterOptions = {}
): string {
	if (typeof value === "string") value = parseFormattedNumber(value)
	if (value == null || isNaN(value)) {
		return treatNullAsZero ? "0" : "-"
	}

	return formatWithPrecision(value, getPrecisionSettings()).display
}

export function resultColorByMinMax(value: number | null, minValue: number, maxValue: number): string {
	return ""
}

/**
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h (hue) is in [0, 360], s (saturation) and l (lightness) are in [0, 1].
 * Returns r, g, and b in the set [0, 255].
 *
 * @param   h - The hue (0-360 degrees).
 * @param   s - The saturation (0-1 percentage).
 * @param   l - The lightness (0-1 percentage).
 * @return  An array representing [r, g, b].
 */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
	let r: number, g: number, b: number

	if (s === 0) {
		r = g = b = l // achromatic
	} else {
		const hueToRgb = (p: number, q: number, t: number): number => {
			let currentT = t
			if (currentT < 0) currentT += 1
			if (currentT > 1) currentT -= 1
			if (currentT < 1 / 6) return p + (q - p) * 6 * currentT
			if (currentT < 1 / 2) return q
			if (currentT < 2 / 3) return p + (q - p) * (2 / 3 - currentT) * 6
			return p
		}

		const q: number = l < 0.5 ? l * (1 + s) : l + s - l * s
		const p: number = 2 * l - q
		// Normalize hue to be between 0 and 1
		const hNormalized: number = h / 360
		r = hueToRgb(p, q, hNormalized + 1 / 3)
		g = hueToRgb(p, q, hNormalized)
		b = hueToRgb(p, q, hNormalized - 1 / 3)
	}

	return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)]
}

/**
 * Converts an RGB color value to HEX.
 * Assumes r, g, and b are contained in the set [0, 255] and
 * returns a 6-digit hex string.
 *
 * @param   r - The red color value (0-255).
 * @param   g - The green color value (0-255).
 * @param   b - The blue color value (0-255).
 * @return  The HEX representation (e.g., "#FF0000").
 */
function rgbToHex(r: number, g: number, b: number): string {
	const componentToHex = (c: number): string => {
		const hex: string = c.toString(16)
		return hex.length === 1 ? "0" + hex : hex
	}
	return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b)
}

/**
 * Calculates a color in a red-yellow-green spectrum based on a value's position
 * within a given range. Red corresponds to the highest value in the range,
 * and green to the lowest. Colors are chosen for black text legibility.
 *
 * @param value - The current value to be colored.
 * @param minValue - The minimum value in the range.
 * @param maxValue - The maximum value in the range.
 * @returns A hex color string (e.g., "#FF8080").
 */
export function getConditionalFormatColor(value: number, minValue: number, maxValue: number): string {
	if (isNaN(value) || minValue === null || maxValue === null || value === null) {
		return "unset"
	}

	const saturation: number = 1.0 // Use full saturation for vibrant colors.
	const lightness: number = 0.8 // Use a high lightness for good contrast with black text.
	// 0.5 is pure color, 1.0 is white. 0.75 is a light shade.

	// Determine the actual numerical minimum and maximum of the provided range boundaries.
	// This handles cases where minValue might be greater than maxValue.
	const actualMin: number = Math.min(minValue, maxValue)
	const actualMax: number = Math.max(minValue, maxValue)

	// Handle the edge case where the range has zero width (min === max).
	if (actualMin === actualMax) {
		// If min and max are the same, all values are effectively equivalent.
		// We'll default to green, implying it's at the "lowest" end of a conceptual scale.
		// Hue 120 is green.
		const [r, g, b]: [number, number, number] = hslToRgb(120, saturation, lightness)
		return rgbToHex(r, g, b) // e.g., #80FF80 for L=0.75, S=1.0
	}

	// Clamp the current value to be within the actual range [actualMin, actualMax].
	// Values outside the range will be colored as if they are at the nearest boundary.
	const clampedValue: number = Math.max(actualMin, Math.min(actualMax, value))

	// Calculate the percentage of the clamped value within the range.
	// 0% means the value is at actualMin (should be green).
	// 100% means the value is at actualMax (should be red).
	const percentage: number = (clampedValue - actualMin) / (actualMax - actualMin)

	// Interpolate the hue.
	// Green is at HSL hue 120. Red is at HSL hue 0.
	// As 'percentage' goes from 0 (lowest value) to 1 (highest value),
	// the hue should go from 120 (green) down to 0 (red).
	// Formula: hue = hue_green * (1 - percentage) + hue_red * percentage
	// hue = 120 * (1 - percentage) + 0 * percentage
	const hue: number = 120 * (1 - percentage)

	// Convert the calculated HSL color to RGB, then to Hex.
	const [r_val, g_val, b_val]: [number, number, number] = hslToRgb(hue, saturation, lightness)
	let finalHex = rgbToHex(r_val, g_val, b_val)
	return finalHex
}

export interface ColumnMinMax {
	columnName: string
	min: number
	max: number
}

export function BuildMinMaxValues<T>(resultList: Array<T>) {
	let columnMinMax: ColumnMinMax[] = []
	resultList.forEach((reportValue) => {
		const columnNames = Object.keys(reportValue as keyof T)
		columnNames.forEach((columnName) => {
			const value = reportValue[columnName as keyof T]
			if (typeof value === "number") {
				const minMax = columnMinMax.find((item) => item.columnName === columnName)
				if (minMax) {
					minMax.min = Math.min(minMax.min, value)
					minMax.max = Math.max(minMax.max, value)
				} else {
					columnMinMax.push({ columnName, min: value, max: value })
				}
			}
		})
	})
	return columnMinMax
}

export function BuildMinMaxValuesFromEmissionFactors(emissionFactors: Array<{ [key: string]: number }>) {
	let columnMinMax: ColumnMinMax[] = []
	emissionFactors.forEach((ef) => {
		if (!ef || typeof ef !== "object") return
		for (const [key, value] of Object.entries(ef)) {
			if (typeof value === "number") {
				const minMax = columnMinMax.find((item) => item.columnName === key)
				if (minMax) {
					minMax.min = Math.min(minMax.min, value)
					minMax.max = Math.max(minMax.max, value)
				} else {
					columnMinMax.push({ columnName: key, min: value, max: value })
				}
			}
		}
	})
	return columnMinMax
}
